import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { type LucideIcon } from 'lucide-react';

interface StatsCardProps {
  title: string;
  count: number | string;
  subtitle?: string;
  linkTo?: string;
  icon: LucideIcon;
  color?: string;
  onClick?: () => void;
  trend?: string;
  trendTone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}

export default function StatsCard({
  title,
  count,
  subtitle,
  linkTo,
  icon: Icon,
  color = 'bg-blue-600',
  onClick,
  trend,
  trendTone = 'neutral',
}: StatsCardProps) {
  const [pop, setPop] = useState(false);
  const prevCount = useRef(count);

  const trendClasses = trendTone === 'success'
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    : trendTone === 'warning'
      ? 'bg-amber-50 text-amber-700 border border-amber-200'
      : trendTone === 'danger'
        ? 'bg-red-50 text-red-700 border border-red-200'
        : trendTone === 'info'
          ? 'bg-blue-50 text-blue-700 border border-blue-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200';

  useEffect(() => {
    if (prevCount.current !== count) {
      setPop(true);
      const t = setTimeout(() => setPop(false), 300);
      prevCount.current = count;
      return () => clearTimeout(t);
    }
  }, [count]);

  const content = (
    <div className="bg-white rounded-lg p-5 block transition-all duration-200 hover:-translate-y-0.5"
         style={{ boxShadow: 'var(--shadow-card)' }}
         onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-card-hover)'; }}
         onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center min-w-0">
          <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="ml-4 flex-1 min-w-0">
            <div className={`text-2xl font-bold text-gray-900 ${pop ? 'animate-count-pop' : ''}`}>
              {count}
            </div>
            <div className="text-sm font-medium text-gray-700 truncate">{title}</div>
            {subtitle && (
              <div className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</div>
            )}
          </div>
        </div>
        {trend && (
          <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${trendClasses}`}>
            {trend}
          </span>
        )}
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo} className="block">{content}</Link>;
  }
  if (onClick) {
    return <button onClick={onClick} className="block w-full text-left">{content}</button>;
  }
  return content;
}
